import { useQuery } from "@tanstack/react-query";
import { parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PadsplitSendHealth } from "./types";

export function PadsplitSendHealthPanel() {
  const healthQuery = useQuery<PadsplitSendHealth>({
    queryKey: ["/api/app/admin/padsplit-send-health"],
    queryFn: async () => {
      const res = await fetch("/api/app/admin/padsplit-send-health", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load PadSplit send health");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const data = healthQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-padsplit-send-health-title">
            PadSplit Send Health
          </h3>
          <p className="text-sm text-muted-foreground">
            On-demand counts of the two PadSplit <code>messages_pending</code> send-pipeline failure modes that
            otherwise only surface via the hourly Sentry cron. <strong>Orphaned</strong> rows were published to the
            events API but never sent (<code>attempts &ge; 1</code>, no <code>sent_at</code>) — the upstream Lambda may
            have dropped the publish. <strong>Misrouted</strong> rows were locally-stamped <code>sent_at</code> but
            never published (<code>attempts = 0</code>) — marked "sent" in the host UI without egressing to PadSplit
            Messenger.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => healthQuery.refetch()}
          disabled={healthQuery.isFetching}
          data-testid="button-refresh-padsplit-send-health"
        >
          {healthQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {healthQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : healthQuery.error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600" data-testid="text-padsplit-send-health-error">
            {parseErrorMessage(healthQuery.error, "Failed to load PadSplit send health")}
          </p>
        </Card>
      ) : data ? (
        <Card className="p-6" data-testid="card-padsplit-send-health-summary">
          <div className="grid gap-6 sm:grid-cols-2">
            {/* Orphaned: published but never sent */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {data.orphaned.orphaned === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
                <span className="font-semibold tabular-nums" data-testid="text-padsplit-send-health-orphaned-count">
                  {data.orphaned.orphaned}
                </span>
                <span className="text-sm text-muted-foreground">
                  orphaned (published &gt; {Math.round(data.orphaned.thresholdMs / 60000)} min ago, never sent)
                </span>
              </div>
              <div className="flex items-center gap-2 pl-7">
                <span className="text-sm text-muted-foreground">Oldest pending_id:</span>
                <span className="text-sm tabular-nums" data-testid="text-padsplit-send-health-orphaned-oldest-id">
                  {data.orphaned.oldestPendingId ?? "—"}
                </span>
              </div>
              <div className="flex items-center gap-2 pl-7">
                <span className="text-sm text-muted-foreground">Oldest updated_at:</span>
                <span className="text-sm tabular-nums" data-testid="text-padsplit-send-health-orphaned-oldest-at">
                  {data.orphaned.oldestUpdatedAt ? new Date(data.orphaned.oldestUpdatedAt).toLocaleString() : "—"}
                </span>
              </div>
            </div>

            {/* Misrouted: locally-stamped but never published */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {data.misrouted.misrouted === 0 ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
                <span className="font-semibold tabular-nums" data-testid="text-padsplit-send-health-misrouted-count">
                  {data.misrouted.misrouted}
                </span>
                <span className="text-sm text-muted-foreground">
                  misrouted (last {data.misrouted.lookbackDays}d, never published)
                </span>
              </div>
              <div className="flex items-center gap-2 pl-7">
                <span className="text-sm text-muted-foreground">Oldest pending_id:</span>
                <span className="text-sm tabular-nums" data-testid="text-padsplit-send-health-misrouted-oldest-id">
                  {data.misrouted.oldestPendingId ?? "—"}
                </span>
              </div>
              <div className="flex items-center gap-2 pl-7">
                <span className="text-sm text-muted-foreground">Oldest sent_at:</span>
                <span className="text-sm tabular-nums" data-testid="text-padsplit-send-health-misrouted-oldest-at">
                  {data.misrouted.oldestSentAt ? new Date(data.misrouted.oldestSentAt).toLocaleString() : "—"}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t flex items-center">
            <span className="text-xs text-muted-foreground" data-testid="text-padsplit-send-health-generated-at">
              Updated {new Date(data.generatedAt).toLocaleTimeString()}
            </span>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
